import React from "react";
import DateConverter from "@heartinz/date_and_time_processor";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import {
  loadImageDimensions,
  urlToBase64,
  capitalizeFirstLetter,
  joinAddress,
  wrapText,
  parseAmount,
} from "../modules/invoiceUtils";
import uRLs from "../../utils/baseUtils";
import { usePermission } from "../../context/PermissionContext";

export const generateInvStrBillingPdf = async (
  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings,
) => {
  const { documentConfig, isPreSale } = invoiceConfig;

  const doc = new jsPDF("p", "mm", "a4");

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const ensureSpace = (
    doc,
    currentY,
    requiredHeight,
    topMargin = 10,
    bottomMargin = 10,
  ) => {
    const pageH = doc.internal.pageSize.getHeight();
    if (currentY + requiredHeight > pageH - bottomMargin) {
      doc.addPage();
      return topMargin;
    }
    return currentY;
  };

  const drawLabelValue = (
    doc,
    label,
    value,
    x,
    y,
    maxWidth,
    lh,
    labelBold = true, // 👈 default bold
  ) => {
    if (!value) return y;

    const labelText = `${label}: `;

    doc.setFont(
      labelBold ? "Inter-Bold" : "Inter-Regular",
      labelBold ? "bold" : "normal",
    );

    const labelWidth = doc.getTextWidth(labelText);
    doc.text(labelText, x, y);

    doc.setFont("Inter-Regular", "normal");
    const wrapped = doc.splitTextToSize(value, maxWidth - labelWidth);
    doc.text(wrapped, x + labelWidth, y);

    return y + wrapped.length * lh;
  };
   const padding=5;
  /* -------------------------------------------------
     HEADER (AUTO HEIGHT)
  ------------------------------------------------- */
  const borderColor = "#C0C0C0";
  const headerY = padding;

  const leftBoxX = padding;
  const leftBoxW = 110; //  reduced width
  const rightBoxX = leftBoxX + leftBoxW + 2;
  const rightBoxW = pageW - rightBoxX - padding;

  const titleHeight = 8;
  const lh = 3.8;

  const headerPaddingTop = 2;
  const headerPaddingBottom = 1;

  const contentX = leftBoxX + 32;
  const maxTextWidth = leftBoxW - 34;

  const address = joinAddress(
    documentConfig?.fromData?.addressline1,
    documentConfig?.fromData?.addressline2,
    documentConfig?.fromData?.district,
    documentConfig?.fromData?.state,
    documentConfig?.fromData?.pincode,
  );

  let tempHeight = headerPaddingTop + titleHeight;

  // company name font size effect
  if (address) {
    tempHeight += doc.splitTextToSize(address, maxTextWidth).length * lh;
  }

  [
    documentConfig?.fromData?.phone,
    documentConfig?.fromData?.email,
    documentConfig?.fromData?.gstnumber,
    documentConfig?.fromData?.fssai,
    documentConfig?.fromData?.cin,
    documentConfig?.fromData?.pannumber,
  ].forEach((v) => {
    if (v) tempHeight += lh;
  });

  tempHeight += headerPaddingBottom;
  const logoBlockHeight = documentConfig?.image
    ? headerPaddingTop + 18 + headerPaddingBottom
    : 0;

  const metaRowHeight = 6; // each row height
  const metaTopSpacing = 5; // space from top before first row
  const metaRows = 5; // Invoice#, Date, POS, Due Date, Sales Person
  const rightMinHeight = metaTopSpacing + metaRows * metaRowHeight + 1;

  // ---- Final Header Height ---- //
  const headerH = Math.max(tempHeight, logoBlockHeight, rightMinHeight) - 1;

  // LEFT HEADER BOX
  const hexToRgb = (hex) => {
    if (!hex) return [0, 0, 0];
    const h = hex.replace("#", "");
    return [
      parseInt(h.substring(0, 2), 16),
      parseInt(h.substring(2, 4), 16),
      parseInt(h.substring(4, 6), 16),
    ];
  };

  const [r, g, b] = hexToRgb(borderColor);

  // Apply border color
  doc.setDrawColor(r, g, b);
  doc.setLineWidth(0.3);
  doc.rect(leftBoxX, headerY, leftBoxW, headerH);

  // LOGO

  if (documentConfig?.image) {
    try {
      const imagePath = documentConfig?.image;

      // Convert to base64
      const base64 = await urlToBase64(imagePath);

      // Natural dimensions
      const { width, height } = await loadImageDimensions(base64);

      // 🔥 Max logo area inside header box
      const maxWidth = 26;
      const maxHeight = headerH - 10;

      let imgW = width;
      let imgH = height;

      const ratio = Math.min(maxWidth / imgW, maxHeight / imgH);
      imgW *= ratio;
      imgH *= ratio;

      // Detect format
      const ext = imagePath.split(".").pop().toLowerCase();
      const format =
        ext === "png"
          ? "PNG"
          : ext === "jpg" || ext === "jpeg"
            ? "JPEG"
            : "JPEG";

      // Draw image
      doc.addImage(base64, format, leftBoxX + 2, headerY + 5, imgW, imgH);
    } catch (err) {
      console.warn("Logo load failed:", err);
    }
  }

  // COMPANY NAME
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(8);
  doc.text(
    documentConfig?.fromData?.title || "",
    contentX,
    headerY + headerPaddingTop + 4,
  );
const fieldGap = 0.5;
  // COMPANY DETAILS
  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(9);

  let cty = headerY + headerPaddingTop + titleHeight+fieldGap;
  if (address) {
    doc.setFont("Inter-Regular", "normal");
    const addrLines = doc.splitTextToSize(address, maxTextWidth);
    doc.text(addrLines, contentX, cty);
    cty += addrLines.length * lh;
  }

  // CONTACT & TAX INFO
  cty = drawLabelValue(
    doc,
    "Phone",
    `${documentConfig?.fromData?.phone ? `+91 ${documentConfig.fromData.phone}` : ""}${
      documentConfig?.fromData?.additionalPhone
        ? `${documentConfig?.fromData?.phone ? ", " : ""}+91 ${documentConfig.fromData.additionalPhone}`
        : ""
    }`,
    contentX,
    cty,
    maxTextWidth,
    lh,
    false, // 👈 normal
  )+fieldGap;

  cty = drawLabelValue(
    doc,
    "Email",
    documentConfig?.fromData?.email,
    contentX,
    cty,
    maxTextWidth,
    lh,
    false, // 👈 normal
  )+fieldGap;

  cty = drawLabelValue(
    doc,
    "GST",
    documentConfig?.fromData?.gstnumber,
    contentX,
    cty,
    maxTextWidth,
    lh,
    true,
  )+fieldGap;

  cty = drawLabelValue(
    doc,
    "FSSAI",
    documentConfig?.fromData?.fssai,
    contentX,
    cty,
    maxTextWidth,
    lh,
    false,
  )+fieldGap;

  cty = drawLabelValue(
    doc,
    "CIN",
    documentConfig?.fromData?.cin,
    contentX,
    cty,
    maxTextWidth,
    lh,
    false,
  )+fieldGap;

  cty = drawLabelValue(
    doc,
    "PAN",
    documentConfig?.fromData?.pannumber,
    contentX,
    cty,
    maxTextWidth,
    lh,
    false,
  )+fieldGap;

  // RIGHT HEADER BOX
  doc.rect(rightBoxX, headerY, rightBoxW, headerH);
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(9);

  // Decide title text
  const invoiceText =
    data?.status === "CANCELLED"
      ? "CANCELLED"
      : documentConfig?.title
        ? documentConfig?.title // eg: TAX INVOICE
        : "";

  // Optional: make CANCELLED stand out
  if (data?.status === "CANCELLED") {
    doc.setTextColor(200, 0, 0); // red
  }

  doc.text(invoiceText, rightBoxX + rightBoxW - 2, headerY + 5, {
    align: "right",
  });

  // reset color for rest of document
  doc.setTextColor(0, 0, 0);

  const invDate = documentConfig?.Date?.date;

  const meta = [
    ["Bill No", documentConfig?.number],
    ["Date", invDate],
    ["Place Of Supply", documentConfig?.fromData?.state || "-"],
    ["Due Date", invDate],
    ["Sales Person", data?.salesperson || ""],
  ];

  let my = headerY + 9;
  meta.forEach(([k, v]) => {
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(9);
    doc.text(k, rightBoxX + 2, my);
    doc.text(":", rightBoxX + 30, my);

    doc.setFont("Inter-Regular", "normal");
    doc.text(String(v || "-"), rightBoxX + 34, my);
    my += 6;
  });

  /* -------------------------------------------------
     CUSTOMER & SHIPPING (AUTO HEIGHT)
  ------------------------------------------------- */
  const pagePadding = padding;
  const gapBetweenBoxes = 2;
  const innerPadding = 2;
  const titleGap = 4; // space below title

  // total available width
  const availableWidth = pageW - pagePadding * 2 - gapBetweenBoxes;
  const boxWidth = availableWidth / 2;

  //  separate variables (no conflict with header)
  const custBoxX = pagePadding;
  const custBoxW = boxWidth;

  const shipBoxX = custBoxX + custBoxW + gapBetweenBoxes;
  const shipBoxW = boxWidth;

  const boxY = headerY + headerH + 2;
  let tableStartY;

  const hasSaleTo =
    documentConfig?.toData &&
    (documentConfig?.toData.name ||
      documentConfig?.toData.addressline1 ||
      documentConfig?.toData.addressline2 ||
      documentConfig?.toData.district ||
      documentConfig?.toData.state ||
      documentConfig?.toData.pincode ||
      documentConfig?.toData.gstnumber);

  if (hasSaleTo) {
    const lineHeight = 3;

    const innerTextWidthLeft = custBoxW - 4;
    const innerTextWidthRight = shipBoxW - 4;

    // ---------- CUSTOMER TEXT

    const custLoc = [
      [documentConfig?.toData?.district, documentConfig?.toData?.state]
        .filter(Boolean)
        .join(", "),
      documentConfig?.toData?.pincode,
    ]
      .filter(Boolean)
      .join(" - ");
    const addressStr = joinAddress(
      documentConfig?.toData?.addressline1,
      documentConfig?.toData?.addressline2,
    );

    const customerText = [
      documentConfig?.toData?.name && {
        label: null,
        value: documentConfig?.toData.name,
      },
      addressStr && { label: null, value: addressStr },
      custLoc && { label: null, value: custLoc },
      documentConfig?.toData?.phone && {
        label: "Phone",
        value: documentConfig?.toData.phone,
      },
      documentConfig?.toData?.email && {
        label: "Email",
        value: documentConfig?.toData.email,
      },
      documentConfig?.toData?.referenceid && {
        label: "Reference No",
        value: documentConfig?.toData.referenceid,
      },
      documentConfig?.toData?.gstnumber && {
        label: "GSTIN",
        value: documentConfig?.toData.gstnumber,
      },
      documentConfig?.toData?.fssai && {
        label: "FSSAI",
        value: documentConfig?.toData.fssai,
      },
      documentConfig?.toData?.pannumber && {
        label: "PAN",
        value: documentConfig?.toData.pannumber,
      },
      documentConfig?.toData?.cinnumber && {
        label: "CIN",
        value: documentConfig?.toData.cinnumber,
      },
    ].filter((item) =>
      Boolean(item && item.value && String(item.value).trim()),
    );
    // const customerText = [
    //   { label: null, value: documentConfig?.toData?.name },
    //   {
    //     label: null,
    //     value: joinAddress(
    //       documentConfig?.toData?.addressline1,
    //       documentConfig?.toData?.addressline2,
    //     ),
    //   },
    //   {
    //     label: null,
    //     value: `${documentConfig?.toData?.district}, ${documentConfig?.toData?.state} - ${documentConfig?.toData?.pincode}`,
    //   },
    //   documentConfig?.toData?.phone && {
    //     label: "Phone",
    //     value: documentConfig?.toData.phone,
    //   },
    //   documentConfig?.toData?.email && {
    //     label: "Email",
    //     value: documentConfig?.toData.email,
    //   },
    //   documentConfig?.toData?.referenceid && {
    //     label: "Reference No ",
    //     value: documentConfig?.toData.referenceid,
    //   },
    //   documentConfig?.toData?.gstnumber && {
    //     label: "GSTIN",
    //     value: documentConfig?.toData.gstnumber,
    //   },
    //   documentConfig?.toData?.fssai && {
    //     label: "FASSI",
    //     value: documentConfig?.toData.fssai,
    //   },
    //   documentConfig?.toData?.pannumber && {
    //     label: "PAN",
    //     value: documentConfig?.toData.pannumber,
    //   },
    //   documentConfig?.toData?.cinnumber && {
    //     label: "CIN",
    //     value: documentConfig?.toData.cinnumber,
    //   },
    // ].filter(Boolean);

    let customerLineCount = 0;
    customerText.forEach((item) => {
      const textToWrap = item.label
        ? `${item.label} : ${item.value}`
        : String(item.value);
      customerLineCount += wrapText(doc, textToWrap, innerTextWidthLeft).length;
    });

    const ship = documentConfig?.toData?.shippingAddress;
    const shipLoc = [
      [ship?.district, ship?.city, ship?.state, ship?.country]
        .filter(Boolean)
        .join(", "),
      ship?.pincode,
    ]
      .filter(Boolean)
      .join(" - ");
    const shippingText = [
      documentConfig?.toData?.shippingAddress?.name,
      joinAddress(
        documentConfig?.toData?.shippingAddress?.addressline1,
        documentConfig?.toData?.shippingAddress?.addressline2,
      ),
      shipLoc,
      data?.transport || "",
    ].filter((val) => Boolean(val && String(val).trim()));

    let shippingLines = [];
    shippingText.forEach((t) => {
      shippingLines.push(...wrapText(doc, t, innerTextWidthRight));
    });
    const shippingLineCount = shippingLines.length;

    // ---------- HEIGHT CALCULATION ----//
    const contentStartOffset = innerPadding + 2 + titleGap;

    const custBoxH = contentStartOffset + customerLineCount * lineHeight + 2;

    const shipBoxH = contentStartOffset + shippingLineCount * lineHeight + 2;

    const boxH = Math.max(custBoxH, shipBoxH);

    // ---------- CUSTOMER BOX

    doc.rect(custBoxX, boxY, custBoxW, boxH);

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8);
    doc.text(
      documentConfig?.toLabel,
      custBoxX + innerPadding,
      boxY + innerPadding + 3,
    );

    let cy = boxY + innerPadding + 3 + titleGap;
    const boldLabels = ["GSTIN", "FASSI", "PAN", "CIN"];

    customerText.forEach((item, idx) => {
      if (!item.label) {
        doc.setFont("Inter-Regular", "normal");
        // doc.setFont(
        //   idx === 0 ? "Inter-Bold" : "Inter-Regular",
        //   idx === 0 ? "bold" : "normal",
        // );
        // name / address
        const lines = wrapText(doc, item.value, innerTextWidthLeft);
        lines.forEach((line) => {
          doc.text(line, custBoxX + innerPadding, cy);
          cy += lineHeight;
        });
      } else {
        const isBold = boldLabels.includes(item.label);

        // label
        doc.setFont(
          isBold ? "Inter-Bold" : "Inter-Regular",
          isBold ? "bold" : "normal",
        );
        doc.text(`${item.label} :`, custBoxX + innerPadding, cy);

        const labelW = doc.getTextWidth(`${item.label} : `);

        // value always normal
        doc.setFont("Inter-Regular", "normal");
        doc.text(item.value, custBoxX + innerPadding + labelW, cy);

        cy += lineHeight;
      }
    });

    // ---------- SHIPPING BOX

    if (
      documentConfig?.toData?.shippingAddress?.addressline1 ||
      documentConfig?.toData?.shippingAddress?.addressline2
    ) {
      doc.rect(shipBoxX, boxY, shipBoxW, boxH);
      doc.setFont("Inter-Bold", "bold");
      doc.text(
        "Shipping Address",
        shipBoxX + innerPadding,
        boxY + innerPadding + 3,
      );

      doc.setFont("Inter-Regular", "normal");
      let sy = boxY + innerPadding + 4 + titleGap;

      shippingLines.forEach((line) => {
        doc.text(line, shipBoxX + innerPadding, sy);
        sy += lineHeight;
      });
    }
    tableStartY = boxY + boxH + 2;
  } else {
    tableStartY = boxY;
  }

  /* -------------------------------------------------
     PRODUCT TABLE
  ------------------------------------------------- */
  autoTable(doc, {
    startY: tableStartY,
    theme: "grid",
    margin: padding,
    styles: {
      font: "Inter-Regular",
      fontSize: 7,
      cellPadding: 1,
      valign: "middle",
      overflow: "linebreak",
      lineWidth: 0.2,
      textColor: "#000000",
    },
    headStyles: {
      font: "Inter-Bold",
      fontSize: 7,
      halign: "center",
      valign: "middle",
      cellPadding: 0.8,
      fillColor: selectedTheme?.templateTheme?.primaryColor,
      textColor: selectedTheme?.templateTheme?.foregroundColor,
    },
    head: [
      [
        "S.No",
        "Description",
        "HSN/SAC",
        "MRP",
        "Discount",
        "Rate",
        "Qty",
        "Amount",
      ],
    ],
    body: documentConfig?.items?.map((it, i) => {
      const amount = it.lineGrandTotal;
      const mrp = it.mrp;
      const Discount = it.discount;
      const rate = it.soldprice;
      const landingCost = it.lineTotalLandingCost;

      return [
        i + 1,
        [it.title, it.note ? `-(${it.note})` : ""].filter(Boolean),
        it.hsnsac || "",
        `${mrp}`,
        `${Discount}`,
        `${rate}`,
        `${it.quantity} ${(() => {
          const unitCode = it?.isSecondaryUnit
            ? it?.unitConfig?.secondaryUnitCode
            : it?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `${amount}`,
      ];
    }),
    columnStyles: {
      0: { cellWidth: 13, halign: "center" },
      1: { cellWidth: "auto", halign: "left" },
      2: { cellWidth: 16, halign: "center" },
      3: { cellWidth: 21, halign: "right" },
      4: { cellWidth: 21, halign: "right" },
      5: { cellWidth: 21, halign: "right" },
      6: { cellWidth: 12, halign: "center" },
      7: { cellWidth: 21, halign: "right" },
      8: { cellWidth: 21, halign: "right" },
    },
  });

  /* -------------------------------------------------
     TOTALS
  ------------------------------------------------- */
  const grantotal = documentConfig?.grandTotal;

  const paymentRows = [];

  // condition based rows
  if (parseAmount(documentConfig?.cashPaid) > 0) {
    paymentRows.push(["Cash", `${documentConfig?.cashPaid}`]);
  }

  if (parseAmount(documentConfig?.cardPaid) > 0) {
    paymentRows.push(["Card", `${documentConfig?.cardPaid}`]);
  }

  if (parseAmount(documentConfig?.onlinePaid) > 0) {
    paymentRows.push(["Online Amount", `${documentConfig?.onlinePaid}`]);
  }

  if (parseAmount(documentConfig?.upiPaid) > 0) {
    paymentRows.push(["UPI", `${documentConfig?.upiPaid}`]);
  }

  if (parseAmount(documentConfig?.balanceDue) > 0) {
    paymentRows.push(["Balance Due", `${documentConfig?.balanceDue}`]);
  }

  let leftY = doc.lastAutoTable.finalY + 4;
  let rightY = doc.lastAutoTable.finalY + 4;

  const valueX = pageW - padding;
  const labelX = valueX - 25;
  rightY = ensureSpace(doc, rightY, 35);

  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(9);
  // Grand Total
  doc.setFont("Inter-Bold", "bold");
  doc.text("Total Amount :", labelX, rightY, { align: "right" });
  doc.text(`${grantotal}`, valueX, rightY, {
    align: "right",
  });
  rightY += 8;

  rightY = ensureSpace(doc, rightY, 25);
  const signBlockY = rightY;

  if (storeSettings?.invoiceSettings?.signature) {
    try {
      const signimg = uRLs.IMAGE_URL + storeSettings.invoiceSettings.signature;
      const base64 = await urlToBase64(signimg);
      const { width, height } = await loadImageDimensions(base64);
      const maxWidth = 40;
      const maxHeight = 10;
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      const imgW = width * ratio;
      const imgH = height * ratio;
      doc.addImage(
        base64,
        signimg.endsWith(".png") ? "PNG" : "JPEG",
        pageW - imgW,
        signBlockY,
        imgW,
        imgH,
      );
    } catch {
      console.warn("Signature load failed");
    }
  }

  doc.setFont("Inter-Regular", "normal");
  doc.text("Issued By, Signature", pageW - padding, signBlockY + 14, {
    align: "right",
  });

  doc.setFont("Inter-Bold", "bold");
  doc.text(documentConfig?.salefrom?.title || "", pageW - padding, signBlockY + 18, {
    align: "right",
  });

  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(9);
  doc.text("Total Items :", leftBoxX, leftY);
  doc.text(String(documentConfig?.totalItems), leftBoxX + 20, leftY);
  leftY += 4;

  leftY = ensureSpace(doc, leftY, 30);
  if (paymentRows.length > 0) {
    autoTable(doc, {
      startY: leftY,
      head: [["Payment Mode", ""]],
      body: paymentRows,
      margin: padding,
      tableWidth: 50,
      styles: {
        font: "Inter-Regular",
        fontSize: 8,
        cellPadding: 1,
        lineColor: selectedTheme?.templateTheme?.borderColor,
        lineWidth: 0.2,
        textColor: "#000",
      },
      headStyles: {
        fillColor: selectedTheme?.templateTheme?.primaryColor,
        textColor: selectedTheme?.templateTheme?.foregroundColor,
        font: "Inter-Bold",
        fontStyle: "bold",
        halign: "center",
      },
      columnStyles: {
        0: { cellWidth: 30, halign: "left" },
        1: { cellWidth: 20, halign: "right" },
      },
      theme: "grid",
    });

    leftY = doc.lastAutoTable.finalY + 5;
  } else {
    leftY += 5;
  }
  if (documentConfig?.bankDetails) {
    leftY = ensureSpace(doc, leftY, 35);
    const bankX = leftBoxX;
    const qrSize = 28;
    const gap = padding;
    let bankY = leftY + 5;

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.text("Bank Details", bankX, leftY);

    const bankDetails = [];

    if (documentConfig?.bankDetails?.accountNo) {
      bankDetails.push({
        label: "Account #",
        value: documentConfig.bankDetails.accountNo,
      });
    }

    if (documentConfig?.bankDetails?.ifsc) {
      bankDetails.push({
        label: "IFSC",
        value: storeSettings.paymentSettings.bankDetails.ifsc,
      });
    }
    if (documentConfig?.bankDetails?.beneficiaryName) {
      bankDetails.push({
        label: "Name",
        value: documentConfig.bankDetails.beneficiaryName,
      });
    }
    if (documentConfig?.bankDetails?.bankName) {
      bankDetails.push({
        label: "Bank Name",
        value: documentConfig.bankDetails.bankName,
      });
    }
    if (documentConfig?.bankDetails?.branch) {
      bankDetails.push({
        label: "Branch Name",
        value: documentConfig.bankDetails.branch,
      });
    }
    if (documentConfig?.upiId) {
      bankDetails.push({
        label: "UPI ID",
        value: documentConfig.upiId,
      });
    }

    const labelWidthOffset = 18;
    let maxWidth = doc.getTextWidth("Bank Details");

    bankDetails.forEach((item) => {
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);
      doc.setTextColor(0, 0, 0);
      doc.text(`${item.label} :`, bankX, bankY);

      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);
      doc.setTextColor(0, 0, 0);
      doc.text(String(item.value || ""), bankX + labelWidthOffset, bankY);

      const completeLineWidth =
        labelWidthOffset + doc.getTextWidth(String(item.value || ""));
      maxWidth = Math.max(maxWidth, completeLineWidth);

      bankY += 4.5;
    });
    doc.setTextColor(0, 0, 0);

    if (qrCodeDataUrl) {
      const qrX = bankX + maxWidth + gap;
      const qrY = leftY - 2;

      doc.addImage(qrCodeDataUrl, "PNG", qrX, qrY, qrSize, qrSize);
    }
    leftY = Math.max(bankY, leftY + qrSize);
  }
  if (documentConfig?.remarks) {
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8);
    doc.setTextColor(72, 72, 72);

    const innerPaddingX = 2;
    // Spans across the full printable width of the page

    const remarkWidth = (pageW - leftBoxX * 2) * 0.65; // 65% of printable width
    const maxTextWidth = remarkWidth - innerPaddingX * 2;
    const remarkLines = doc.splitTextToSize(
      capitalizeFirstLetter(documentConfig?.remarks),
      maxTextWidth,
    );

    const textLineHeight = 3.5;
    const boxPaddingTop = 2.5;
    const boxPaddingBottom = 2.5;

    const remarkHeight =
      boxPaddingTop + remarkLines.length * textLineHeight + boxPaddingBottom;

    // Ensure there's room on the page for the full-width block
    leftY = ensureSpace(doc, leftY, remarkHeight + 6);
    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(0);
    doc.text("Remark", leftBoxX, leftY);

    const boxStartY = leftY + 2;

    // Draw the full-width tight rounded container box
    doc.setDrawColor(180);
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(7);
    doc.setTextColor(0);

    let remarkY = boxStartY + boxPaddingTop;

    remarkLines.forEach((line) => {
      doc.text(line, leftBoxX, remarkY);
      remarkY += textLineHeight;
    });

    // Push vertical tracker below the full-row remark box
    leftY = boxStartY + remarkHeight;
  }
  if (terms?.length > 0 && !isPreSale) {
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(7);

    const termLines = doc.splitTextToSize(terms, pageW / 2 - 12);

    const termsHeight = termLines.length * 3.5 + 6;

    leftY = ensureSpace(doc, leftY, termsHeight);

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8);
    doc.text("Terms & Conditions", leftBoxX, leftY);

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(7);
    doc.text(termLines, leftBoxX, leftY + 4);

    leftY += termsHeight;
  }

  // draw table ONLY if rows exist

  if (storeSettings?.negobillBranding) {
    const y = pageH - 5;
    const rightX = pageW - 5;

    // Text parts
    const normalText = "Powered by ";
    const boldText = "NegoBill.com";

    doc.setFontSize(8);

    // Measure widths
    doc.setFont("Inter-Regular", "normal");
    const normalW = doc.getTextWidth(normalText);

    doc.setFont("Inter-Bold", "bold");
    const boldW = doc.getTextWidth(boldText);

    const totalW = normalW + boldW;

    // Start X so whole string is right-aligned
    const startX = rightX - totalW;

    // Draw normal part
    doc.setFont("Inter-Regular", "normal");
    doc.text(normalText, startX, y);

    // Draw bold part
    doc.setFont("Inter-Bold", "bold");
    doc.text(boldText, startX + normalW, y);
  }
  // setDownload(false);
  return doc;
};

export const generateInvStrInvoicePdf = async (
  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings,
) => {
  const { documentConfig, isInterState, isPreSale } = invoiceConfig;

  const doc = new jsPDF("p", "mm", "a4");

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const ensureSpace = (
    doc,
    currentY,
    requiredHeight,
    topMargin = 10,
    bottomMargin = 10,
  ) => {
    const pageH = doc.internal.pageSize.getHeight();
    if (currentY + requiredHeight > pageH - bottomMargin) {
      doc.addPage();
      return topMargin;
    }
    return currentY;
  };

  const drawLabelValue = (
    doc,
    label,
    value,
    x,
    y,
    maxWidth,
    lh,
    labelBold = true, // 👈 default bold
  ) => {
    if (!value) return y;

    const labelText = `${label}: `;

    doc.setFont(
      labelBold ? "Inter-Bold" : "Inter-Regular",
      labelBold ? "bold" : "normal",
    );

    const labelWidth = doc.getTextWidth(labelText);
    doc.text(labelText, x, y);

    doc.setFont("Inter-Regular", "normal");
    const wrapped = doc.splitTextToSize(value, maxWidth - labelWidth);
    doc.text(wrapped, x + labelWidth, y);

    return y + wrapped.length * lh;
  };

  /* -------------------------------------------------
       HEADER (AUTO HEIGHT)
    ------------------------------------------------- */
  const borderColor = "#C0C0C0";
  const headerY = 5;

  const leftBoxX = 5;
  const leftBoxW = 110; // ⬅️ reduced width
  const rightBoxX = leftBoxX + leftBoxW + 2;
  const rightBoxW = pageW - rightBoxX - leftBoxX;

  const titleHeight = 8;
  const lh = 3.8;

  const headerPaddingTop = 2;
  const headerPaddingBottom = 1;

  const contentX = leftBoxX + 32;
  const maxTextWidth = leftBoxW - 34;

  const address = joinAddress(
    documentConfig?.fromData?.addressline1,
    documentConfig?.fromData?.addressline2,
    documentConfig?.fromData?.district,
    documentConfig?.fromData?.state,
    documentConfig?.fromData?.pincode,
  );

  let tempHeight = headerPaddingTop + titleHeight;

  // company name font size effect
  if (address) {
    tempHeight += doc.splitTextToSize(address, maxTextWidth).length * lh;
  }

  [
    documentConfig?.fromData?.phone,
    documentConfig?.fromData?.email,
    documentConfig?.fromData?.gstnumber,
    documentConfig?.fromData?.fssai,
    documentConfig?.fromData?.cin,
    documentConfig?.fromData?.pannumber,
  ].forEach((v) => {
    if (v) tempHeight += lh;
  });

  tempHeight += headerPaddingBottom;
  const logoBlockHeight = documentConfig?.image
    ? headerPaddingTop + 18 + headerPaddingBottom
    : 0;

  // ---- Calculate RIGHT SIDE minimum height ---- //
  const metaRowHeight = 6; // each row height
  const metaTopSpacing = 5; // space from top before first row
  const metaRows = 5; // Invoice#, Date, POS, Due Date, Sales Person
  const rightMinHeight = metaTopSpacing + metaRows * metaRowHeight + 1;

  // ---- Final Header Height ---- //
  const headerH = Math.max(tempHeight, logoBlockHeight, rightMinHeight) - 1;

  // LEFT HEADER BOX
  const hexToRgb = (hex) => {
    if (!hex) return [0, 0, 0];
    const h = hex.replace("#", "");
    return [
      parseInt(h.substring(0, 2), 16),
      parseInt(h.substring(2, 4), 16),
      parseInt(h.substring(4, 6), 16),
    ];
  };

  const [r, g, b] = hexToRgb(borderColor);

  // Apply border color
  doc.setDrawColor(r, g, b);
  doc.setLineWidth(0.3);
  doc.rect(leftBoxX, headerY, leftBoxW, headerH);

  // LOGO

  if (documentConfig?.image) {
    try {
      const imagePath =  documentConfig?.image;

      // Convert to base64
      const base64 = await urlToBase64(imagePath);

      // Natural dimensions
      const { width, height } = await loadImageDimensions(base64);

      //  Max logo area inside header box
      const maxWidth = 26;
      const maxHeight = headerH - 10;

      let imgW = width;
      let imgH = height;

      const ratio = Math.min(maxWidth / imgW, maxHeight / imgH);
      imgW *= ratio;
      imgH *= ratio;

      // Detect format
      const ext = imagePath.split(".").pop().toLowerCase();
      const format =
        ext === "png"
          ? "PNG"
          : ext === "jpg" || ext === "jpeg"
            ? "JPEG"
            : "JPEG";

      // Draw image
      doc.addImage(base64, format, leftBoxX + 2, headerY + 5, imgW, imgH);
    } catch (err) {
      console.warn("Logo load failed:", err);
    }
  }

  // COMPANY NAME
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(8);
  doc.text(
    documentConfig?.fromData?.title || "",
    contentX,
    headerY + headerPaddingTop + 4,
  );

  // COMPANY DETAILS
  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(8);
 const fieldGap = 0.5;
  let cty = headerY + headerPaddingTop + titleHeight+fieldGap;
  if (address) {
    doc.setFont("Inter-Regular", "normal");
    const addrLines = doc.splitTextToSize(address, maxTextWidth);
    doc.text(addrLines, contentX, cty);
    cty += addrLines.length * lh;
  }

  // CONTACT & TAX INFO
  cty = drawLabelValue(
    doc,
    "Phone",
    `${documentConfig?.fromData?.phone ? `+91 ${documentConfig.fromData.phone}` : ""}${
      documentConfig?.fromData?.additionalPhone
        ? `${documentConfig?.fromData?.phone ? ", " : ""}+91 ${documentConfig.fromData.additionalPhone}`
        : ""
    }`,
    contentX,
    cty,
    maxTextWidth,
    lh,
    false, // 👈 normal
  )+fieldGap;;

  cty = drawLabelValue(
    doc,
    "Email",
    documentConfig?.fromData?.email,
    contentX,
    cty,
    maxTextWidth,
    lh,
    false, // 👈 normal
  )+fieldGap;
  cty = drawLabelValue(
    doc,
    "GSTIN",
    documentConfig?.fromData?.gstnumber,
    contentX,
    cty,
    maxTextWidth,
    lh,
    true,
  )+fieldGap;

  cty = drawLabelValue(
    doc,
    "FSSAI",
    documentConfig?.fromData?.fssai,
    contentX,
    cty,
    maxTextWidth,
    lh,
    false,
  )+fieldGap;

  cty = drawLabelValue(
    doc,
    "CIN",
    documentConfig?.fromData?.cin,
    contentX,
    cty,
    maxTextWidth,
    lh,
    false,
  )+fieldGap;

  cty = drawLabelValue(
    doc,
    "PAN",
    documentConfig?.fromData?.pannumber,
    contentX,
    cty,
    maxTextWidth,
    lh,
    false,
  )+fieldGap;

  // RIGHT HEADER BOX
  doc.rect(rightBoxX, headerY, rightBoxW, headerH);
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(9);

  const invoiceText =
    data?.status === "CANCELLED"
      ? "CANCELLED"
      : documentConfig?.title
        ? documentConfig?.title // eg: TAX INVOICE
        : "";

  // Optional: make CANCELLED stand out
  if (data?.status === "CANCELLED") {
    doc.setTextColor(200, 0, 0); // red
  }

  doc.text(invoiceText, rightBoxX + rightBoxW - 2, headerY + 5, {
    align: "right",
  });

  // reset color for rest of document
  doc.setTextColor(0, 0, 0);

  const invDate = documentConfig?.Date?.date;

  const meta = [
    [documentConfig?.numberLabel, documentConfig?.number],
    ["Date", invDate],
    ["Place Of Supply", documentConfig?.fromData?.state || "-"],
    ["Due Date", invDate],
    ["Sales Person", data?.salesperson || ""],
  ];

  let my = headerY + 9;
  meta.forEach(([k, v]) => {
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(9);
    doc.text(k, rightBoxX + 2, my);
    doc.text(":", rightBoxX + 30, my);

    doc.setFont("Inter-Regular", "normal");
    doc.text(String(v || "-"), rightBoxX + 34, my);
    my += 6;
  });

  /* -------------------------------------------------
       CUSTOMER & SHIPPING (AUTO HEIGHT)
    ------------------------------------------------- */
  const pagePadding = leftBoxX;
  const gapBetweenBoxes = 2;
  const innerPadding = 2;
  const titleGap = 4; // space below title

  // total available width
  const availableWidth = pageW - pagePadding * 2 - gapBetweenBoxes;
  const boxWidth = availableWidth / 2;

  //  separate variables (no conflict with header)
  const custBoxX = pagePadding;
  const custBoxW = boxWidth;

  const shipBoxX = custBoxX + custBoxW + gapBetweenBoxes;
  const shipBoxW = boxWidth;

  const boxY = headerY + headerH + 2;
  let tableStartY;

  const hasSaleTo =
    documentConfig?.toData &&
    (documentConfig?.toData.name ||
      documentConfig?.toData.addressline1 ||
      documentConfig?.toData.addressline2 ||
      documentConfig?.toData.district ||
      documentConfig?.toData.state ||
      documentConfig?.toData.pincode ||
      documentConfig?.toData.gstnumber);

  if (hasSaleTo) {
    const lineHeight = 3;

    const innerTextWidthLeft = custBoxW - 4;
    const innerTextWidthRight = shipBoxW - 4;
    const saleto = documentConfig?.toData;
    const locationParts = [saleto?.district, saleto?.state]
      .filter(Boolean)
      .join(", ");
    const locationString = locationParts
      ? `${locationParts}${saleto?.pincode ? ` - ${saleto.pincode}` : ""}`
      : saleto?.pincode || "";
    const customerText = [
      saleto?.name,
      saleto?.addressline1 || saleto?.addressline2
        ? [saleto?.addressline1, saleto?.addressline2]
            .filter(Boolean)
            .join(", ")
        : "",
      locationString,
      saleto?.phone ? `Phone : ${saleto.phone}` : "",
      saleto?.email ? `Email : ${saleto.email}` : "",
      saleto?.referenceid ? `Reference No : ${saleto.referenceid}` : "",
      saleto?.gstnumber ? `GSTIN : ${saleto.gstnumber}` : "",
      saleto?.fssai ? `FSSAI : ${saleto.fssai}` : "",
      saleto?.pannumber ? `PAN : ${saleto.pannumber}` : "",
      saleto?.cinnumber ? `CIN : ${saleto.cinnumber}` : "",
    ].filter(Boolean);
 

    let customerLines = [];
    customerText.forEach((t) => {
      customerLines.push(...wrapText(doc, t, innerTextWidthLeft));
    });

    // ---------- SHIPPING TEXT
    const shippingText = [
      documentConfig?.toData?.shippingAddress?.name,
      joinAddress(
        documentConfig?.toData?.shippingAddress?.addressline1,
        documentConfig?.toData?.shippingAddress?.addressline2,
      ),
      `${documentConfig?.toData?.shippingAddress?.district}, ${documentConfig?.toData?.shippingAddress?.city}, ${documentConfig?.toData?.shippingAddress?.state},${documentConfig?.toData?.shippingAddress?.country} - ${documentConfig?.toData?.shippingAddress?.pincode}`,
      data?.transport || "",
    ].filter(Boolean);

    let shippingLines = [];
    shippingText.forEach((t) => {
      shippingLines.push(...wrapText(doc, t, innerTextWidthRight));
    });

    // ---------- HEIGHT CALCULATION ----//
    const customerTextStartY = boxY + innerPadding + 1 + titleGap;
    const shippingTextStartY = boxY + innerPadding + 1 + titleGap;
    const customerTextHeight = customerLines.length * lineHeight;
    const shippingTextHeight = shippingLines.length * lineHeight;
    const custBoxH = customerTextStartY - boxY + customerTextHeight + 1;

    const shipBoxH = shippingTextStartY - boxY + shippingTextHeight + 1;
    const boxH = Math.max(custBoxH, shipBoxH);

    // ---------- CUSTOMER BOX

    doc.rect(custBoxX, boxY, custBoxW, boxH);

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8);
    doc.text(
      documentConfig?.toLabel,
      custBoxX + innerPadding,
      boxY + innerPadding + 3,
    );

    doc.setFont("Inter-Regular", "normal");
    let cy = boxY + innerPadding + 3 + titleGap;

    customerLines.forEach((line) => {
      doc.text(line, custBoxX + innerPadding, cy);
      cy += lineHeight;
    });

    // ---------- SHIPPING BOX

    if (
      documentConfig?.toData?.shippingAddress?.addressline1 ||
      documentConfig?.toData?.shippingAddress?.addressline2
    ) {
      doc.rect(shipBoxX, boxY, shipBoxW, boxH);
      doc.setFont("Inter-Bold", "bold");
      doc.text(
        "Shipping Address",
        shipBoxX + innerPadding,
        boxY + innerPadding + 3,
      );

      doc.setFont("Inter-Regular", "normal");
      let sy = boxY + innerPadding + 4 + titleGap;

      shippingLines.forEach((line) => {
        doc.text(line, shipBoxX + innerPadding, sy);
        sy += lineHeight;
      });
    }
    tableStartY = boxY + boxH + 2;
  } else {
    tableStartY = boxY;
  }

  /* -------------------------------------------------
       PRODUCT TABLE
    ------------------------------------------------- */
  autoTable(doc, {
    startY: tableStartY,
    theme: "grid",
    margin:leftBoxX,
    styles: {
      font: "Inter-Regular",
      fontSize: 7,
      cellPadding: 1,
      valign: "middle",
      overflow: "linebreak",
      lineWidth: 0.2,
      textColor: "#000000",
    },
    headStyles: {
      font: "Inter-Bold",
      fontSize: 7,
      halign: "center",
      valign: "middle",
      cellPadding: 0.8,
      fillColor: selectedTheme?.templateTheme?.primaryColor,
      textColor: selectedTheme?.templateTheme?.foregroundColor,
    },
    head: [
      [
        "S.No",
        "Description",
        "HSN/SAC",
        "MRP",
        "Base Price",
        "Qty",
        "Tax Val",
        "Tax",
        "Tax Amt",
        "Amount",
      ],
    ],
    body: documentConfig?.items.map((it, i) => {
      const rate = it.lineTotalTaxAmount;
      const amount = it.lineGrandTotal;
      const mrp = it.mrp;
      const baseprice = it.baseprice;
      const landingCost = it?.lineTotalLandingCost;
      const taxableValue = it.lineTotalBasePrice;

      return [
        i + 1,
        [it.title, it.note ? `-(${it.note})` : ""].filter(Boolean),
        it.hsnsac || "",
        `${mrp}`,
        `${baseprice}`,
        `${it.quantity} ${(() => {
          const unitCode = it?.isSecondaryUnit
            ? it?.unitConfig?.secondaryUnitCode
            : it?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `${taxableValue}`,
        `${it.taxpercentage || 0}%`,
        `${rate}`,
        `${amount}`,
      ];
    }),
    columnStyles: {
      0: { cellWidth: 13, halign: "center" },
      1: { cellWidth: "auto", halign: "left" },
      2: { cellWidth: 16, halign: "center" },
      3: { cellWidth: 21, halign: "right" },
      4: { cellWidth: 21, halign: "right" },
      5: { cellWidth: 12, halign: "center" },
      6: { cellWidth: 21, halign: "right" },
      7: { cellWidth: 10, halign: "center" },
      8: { cellWidth: 21, halign: "right" },
      9: { cellWidth: 21, halign: "right" },
    },
  });

  const igst = documentConfig?.igstAmount;
  const sgst = documentConfig?.sgstAmount;
  const cgst = documentConfig?.cgstAmount;
  const linetotalbaseprice = documentConfig?.totalBasePrice;
  const grantotal = documentConfig?.grandTotal;

  const paymentRows = [];

  // condition based rows
  if (parseAmount(documentConfig?.cashPaid) > 0) {
    paymentRows.push(["Cash", `${documentConfig?.cashPaid}`]);
  }

  if (parseAmount(documentConfig?.cardPaid) > 0) {
    paymentRows.push(["Card", `${documentConfig?.cardPaid}`]);
  }

  if (parseAmount(documentConfig?.onlinePaid) > 0) {
    paymentRows.push(["Online Amount", `${documentConfig?.onlinePaid}`]);
  }

  if (parseAmount(documentConfig?.upiPaid) > 0) {
    paymentRows.push(["UPI", `${documentConfig?.upiPaid}`]);
  }

  if (parseAmount(documentConfig?.balanceDue) > 0) {
    paymentRows.push(["Balance Due", `${documentConfig?.balanceDue}`]);
  }
  let startY = doc.lastAutoTable.finalY + 4;
  startY = ensureSpace(doc, startY, 50);
  let leftY = startY;
  let rightY = startY;

  const valueX = pageW - leftBoxX;
  const labelX = valueX - 25;
  rightY = ensureSpace(doc, rightY, 35);

  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(9);

  // Total Taxable
  doc.text("Total Amount Before Tax :", labelX, rightY, { align: "right" });
  doc.text(`${linetotalbaseprice}`, valueX, rightY, {
    align: "right",
  });
  rightY += 5;

  // GST
  if (isInterState) {
    doc.text("IGST :", labelX, rightY, { align: "right" });
    doc.text(`${igst}`, valueX, rightY, { align: "right" });
    rightY += 5;
  } else {
    doc.text("SGST :", labelX, rightY, { align: "right" });
    doc.text(`${sgst}`, valueX, rightY, { align: "right" });
    rightY += 5;

    doc.text("CGST :", labelX, rightY, { align: "right" });
    doc.text(`${cgst}`, valueX, rightY, { align: "right" });
    rightY += 5;
  }

  // Grand Total
  doc.setFont("Inter-Bold", "bold");
  doc.text("Total Amount :", labelX, rightY, { align: "right" });
  doc.text(`${grantotal}`, valueX, rightY, {
    align: "right",
  });
  rightY += 8;

  rightY = ensureSpace(doc, rightY, 25);
  const signBlockY = rightY;

  if (storeSettings?.invoiceSettings?.signature) {
    try {
      const signimg = uRLs.IMAGE_URL + storeSettings.invoiceSettings.signature;
      const base64 = await urlToBase64(signimg);
      const { width, height } = await loadImageDimensions(base64);
      const maxWidth = 40;
      const maxHeight = 10;
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      const imgW = width * ratio;
      const imgH = height * ratio;
      doc.addImage(
        base64,
        signimg.endsWith(".png") ? "PNG" : "JPEG",
        pageW  - imgW,
        signBlockY,
        imgW,
        imgH,
      );
    } catch {
      console.warn("Signature load failed");
    }
  }

  doc.setFont("Inter-Regular", "normal");
  doc.text("Issued By, Signature", pageW - leftBoxX, signBlockY + 14, {
    align: "right",
  });

  doc.setFont("Inter-Bold", "bold");
  doc.text(documentConfig?.fromData?.title || "", pageW - leftBoxX, signBlockY + 18, {
    align: "right",
  });

  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(9);
  doc.text("Total Items :", leftBoxX, leftY);
  doc.text(String(documentConfig?.totalItems), leftBoxX + 20, leftY);
  leftY += 4;

  leftY = ensureSpace(doc, leftY, 30);
  if (paymentRows.length > 0) {
    autoTable(doc, {
      startY: leftY,
      head: [["Payment Mode", ""]],
      body: paymentRows,
      margin: leftBoxX,
      tableWidth: 50,
      styles: {
        font: "Inter-Regular",
        fontSize: 8,
        cellPadding: 1,
        lineColor: selectedTheme?.templateTheme?.borderColor,
        lineWidth: 0.2,
        textColor: "#000",
      },
      headStyles: {
        fillColor: selectedTheme?.templateTheme?.primaryColor,
        textColor: selectedTheme?.templateTheme?.foregroundColor,
        font: "Inter-Bold",
        fontStyle: "bold",
        halign: "center",
      },
      columnStyles: {
        0: { cellWidth: 30, halign: "left" },
        1: { cellWidth: 20, halign: "right" },
      },
      theme: "grid",
    });

    leftY = doc.lastAutoTable.finalY + 5;
  } else {
    leftY += 5;
  }
  // =======================================
  // Bank Details + QR Code
  // =======================================
  if (documentConfig?.bankDetails) {
    leftY = ensureSpace(doc, leftY, 35);
    const bankX = leftBoxX;
    const qrSize = 28;
    const gap = leftBoxX;
    let bankY = leftY + 5;

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.text("Bank Details", bankX, leftY);

    const bankDetails = [];

    if (documentConfig?.bankDetails?.accountNo) {
      bankDetails.push({
        label: "Account #",
        value: documentConfig.bankDetails.accountNo,
      });
    }

    if (documentConfig?.bankDetails?.ifsc) {
      bankDetails.push({
        label: "IFSC",
        value: storeSettings.paymentSettings.bankDetails.ifsc,
      });
    }
    if (documentConfig?.bankDetails?.beneficiaryName) {
      bankDetails.push({
        label: "Name",
        value: documentConfig.bankDetails.beneficiaryName,
      });
    }
    if (documentConfig?.bankDetails?.bankName) {
      bankDetails.push({
        label: "Bank Name",
        value: documentConfig.bankDetails.bankName,
      });
    }
    if (documentConfig?.bankDetails?.branch) {
      bankDetails.push({
        label: "Branch Name",
        value: documentConfig.bankDetails.branch,
      });
    }

    if (documentConfig?.upiId) {
      bankDetails.push({
        label: "UPI ID",
        value: storeSettings.paymentSettings.upiId,
      });
    }

    const labelWidthOffset = 18;
    let maxWidth = doc.getTextWidth("Bank Details");

    bankDetails.forEach((item) => {
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);
      doc.setTextColor(0, 0, 0);
      doc.text(`${item.label} :`, bankX, bankY);

      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);
      doc.setTextColor(0, 0, 0);
      doc.text(String(item.value || ""), bankX + labelWidthOffset, bankY);

      const completeLineWidth =
        labelWidthOffset + doc.getTextWidth(String(item.value || ""));
      maxWidth = Math.max(maxWidth, completeLineWidth);

      bankY += 4.5;
    });
    doc.setTextColor(0, 0, 0);

    if (qrCodeDataUrl) {
      const qrX = bankX + maxWidth + gap;
      const qrY = leftY - 2;

      doc.addImage(qrCodeDataUrl, "PNG", qrX, qrY, qrSize, qrSize);
    }
    leftY = Math.max(bankY, leftY + qrSize);
  }

  if (documentConfig?.remarks) {
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8);
    doc.setTextColor(72, 72, 72);

    const innerPaddingX = 2;
    // Spans across the full printable width of the page

    const remarkWidth = (pageW - leftBoxX * 2) * 0.65; // 65% of printable width
    const maxTextWidth = remarkWidth - innerPaddingX * 2;
    const remarkLines = doc.splitTextToSize(
      capitalizeFirstLetter(documentConfig?.remarks),
      maxTextWidth,
    );

    const textLineHeight = 3.5;
    const boxPaddingTop = 2.5;
    const boxPaddingBottom = 2.5;

    const remarkHeight =
      boxPaddingTop + remarkLines.length * textLineHeight + boxPaddingBottom;

    // Ensure there's room on the page for the full-width block
    leftY = ensureSpace(doc, leftY, remarkHeight + 6) + 1;
    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(0);
    doc.text("Remark", leftBoxX, leftY);

    const boxStartY = leftY + 2;

    // Draw the full-width tight rounded container box
    doc.setDrawColor(180);
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(7);
    doc.setTextColor(0);

    let remarkY = boxStartY + boxPaddingTop;

    remarkLines.forEach((line) => {
      doc.text(line, leftBoxX, remarkY);
      remarkY += textLineHeight;
    });

    // Push vertical tracker below the full-row remark box
    leftY = boxStartY + remarkHeight;
  }
  if (terms?.length > 0 && !isPreSale) {
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(7);

    const termLines = doc.splitTextToSize(terms, pageW / 2 - 12);

    const termsHeight = termLines.length * 3.5 + 6;

    leftY = ensureSpace(doc, leftY, termsHeight);

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8);
    doc.text("Terms & Conditions", leftBoxX, leftY);

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(7);
    doc.text(termLines, leftBoxX, leftY + 4);

    leftY += termsHeight;
  }

  // draw table ONLY if rows exist

  if (storeSettings?.negobillBranding) {
    const y = pageH - 5;
    const rightX = pageW - 5;

    // Text parts
    const normalText = "Powered by ";
    const boldText = "NegoBill.com";

    doc.setFontSize(8);

    // Measure widths
    doc.setFont("Inter-Regular", "normal");
    const normalW = doc.getTextWidth(normalText);

    doc.setFont("Inter-Bold", "bold");
    const boldW = doc.getTextWidth(boldText);

    const totalW = normalW + boldW;

    // Start X so whole string is right-aligned
    const startX = rightX - totalW;

    // Draw normal part
    doc.setFont("Inter-Regular", "normal");
    doc.text(normalText, startX, y);

    // Draw bold part
    doc.setFont("Inter-Bold", "bold");
    doc.text(boldText, startX + normalW, y);
  }
  // setDownload(false);
  return doc;
};


const StructuredInvoice = ({
  globalData,
  terms,
  qrCodeDataUrl,  
  selectedTheme,
  invoiceConfig,
}) => {
  const { documentConfig, showTaxDetails, showBillDetails,isInterState, hasPayment,isPreSale } =
    invoiceConfig;
 
  const { storeSettings } = usePermission();

  return (
    <>
      {showTaxDetails ? (
        <>
          <div className="tm_container">
            <div className="tm_invoice_wrap">
              <div className="tm_invoice tm_style1 tm_type1">
                <div className="tm_invoice_in p-2">
                  <div className="row">
                    <div className="col-7 pe-1 ">
                      <div className="border p-2 h-100 d-flex ">
                        <div className="d-flex gap-2 align-items-start">
                          <div className="tm_logo">
                            {documentConfig?.image && (
                              <img
                                src={
                                  documentConfig?.image
                                }
                                className="img-rounded logo mb-0"
                                height="45"
                                width="150"
                                style={{
                                  objectFit: "contain",
                                }}
                              />
                            )}
                          </div>
                          <div>
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                marginBottom: "0px",
                              }}
                            >
                              {documentConfig?.fromData?.title || ""}
                            </div>
                            <div>
                              {documentConfig?.fromData?.addressline1 || ""}{" "}
                              {documentConfig?.fromData?.addressline2 ||
                                ""}{" "}
                            </div>
                            <div>
                              {documentConfig?.fromData?.state || ""}{" "}
                              {documentConfig?.fromData?.district || ""}{" "}
                              {documentConfig?.fromData?.country || ""} -{" "}
                              {documentConfig?.fromData?.pincode || ""}
                            </div>                                                    
                            <div>
                              Phone: +91 {documentConfig?.fromData?.phone || ""}
                              {documentConfig?.fromData?.additionalPhone && (
                                <>
                                  , +91{" "}
                                  {documentConfig.fromData.additionalPhone}
                                </>
                              )}
                            </div>
                            {documentConfig?.fromData?.email && (
                              <div>
                                {" "}
                                Email: {documentConfig?.fromData?.email || ""}
                              </div>
                            )}
                            {documentConfig?.fromData?.gstnumber && (
                              <div>
                                <strong>GSTIN:</strong>{" "}
                                {documentConfig?.fromData?.gstnumber || ""}
                              </div>
                            )}
                            {documentConfig?.fromData?.fssai && (
                              <div>
                                FSSAI:{" "}
                                {documentConfig?.fromData?.fssai || ""}
                              </div>
                            )}
                            {documentConfig?.fromData?.cinnumber && (
                              <div>
                                CIN:{" "}
                                {documentConfig?.fromData?.cinnumber || ""}
                              </div>
                            )}
                            {documentConfig?.fromData?.pannumber && (
                              <div>
                                PAN:{" "}
                                {documentConfig?.fromData?.pannumber || ""}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-5 ps-1 ">
                      <div className="border h-100 p-2">
                        <div className="tm_text_uppercase text-end">
                          {documentConfig.title && (
                            <strong>{documentConfig.title}</strong>
                          )}
                        </div>
                        <div className="pb-1 d-flex">
                          <b style={{ width: "100px" }}>
                            {documentConfig?.numberLabel}
                          </b>
                          <span>: {documentConfig?.number}</span>
                        </div>

                        <div className="pb-1 d-flex">
                          <b style={{ width: "100px" }}>Date</b>
                          <span>: {documentConfig?.Date?.date}</span>
                        </div>

                        <div className="pb-1 d-flex">
                          <b style={{ width: "100px" }}>Place Of Supply</b>
                          <span>: {documentConfig?.fromData?.state || ""}</span>
                        </div>

                        <div className="pb-1 d-flex">
                          <b style={{ width: "100px" }}>Due Date</b>
                          <span>: {documentConfig?.Date?.date}</span>
                        </div>

                        <div className="pb-1 d-flex">
                          <b style={{ width: "100px" }}>Sales Person</b>
                          <span>:{globalData?.salesperson || "-"}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {documentConfig?.toData && (
                    <div className="row mt-2">
                      <div className="col-6 pe-1">
                        <div className="border p-2 h-100 ">
                          <div>
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                marginBottom: "0px",
                              }}
                            >
                              {documentConfig?.toLabel}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                marginBottom: "0px",
                              }}
                            >
                              {documentConfig?.toData?.name || ""}
                            </div>
                            <div>
                              {documentConfig?.toData?.addressline1 || ""}{" "}
                              {documentConfig?.toData?.addressline2 || ""}{" "}
                            </div>
                            <div>
                              {documentConfig?.toData?.state || ""}{" "}
                              {documentConfig?.toData?.district || ""}{" "}
                              {/* {documentConfig?.toData?.country || ""} -{" "} */}
                              {documentConfig?.toData?.pincode || ""}
                            </div>
                            <div>
                              {" "}
                              Phone: +91 {documentConfig?.toData?.phone || ""}
                            </div>
                            {documentConfig?.toData?.email && (
                              <div>
                                {" "}
                                Email {documentConfig?.toData?.email || ""}
                              </div>
                            )}
                            {documentConfig?.toData?.referenceid && (
                              <div>
                                <span>Reference No:</span>{" "}
                                {documentConfig?.toData?.referenceid || ""}
                              </div>
                            )}
                            {documentConfig?.toData?.gstnumber && (
                              <div>
                                <span>GSTIN:</span>{" "}
                                {documentConfig?.toData?.gstnumber || ""}
                              </div>
                            )}
                            {documentConfig?.toData?.fssai && (
                              <div>
                                <span>FASSI:</span>{" "}
                                {documentConfig?.toData?.fssai || ""}
                              </div>
                            )}
                            {documentConfig?.toData?.pannumber && (
                              <div>
                                <span>PAN:</span>{" "}
                                {documentConfig?.toData?.pannumber || ""}
                              </div>
                            )}
                            {documentConfig?.toData?.cinnumber && (
                              <div>
                                CIN :{documentConfig?.toData?.cinnumber || ""}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {documentConfig?.toData?.shippingAddress?.addressline1 &&
                        documentConfig?.toData?.shippingAddress
                          ?.addressline2 && (
                          <div className="col-6 ps-1">
                            <div className="border p-2 h-100 ">
                              <div>
                                <div
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    marginBottom: "0px",
                                  }}
                                >
                                  Shipping Address
                                </div>
                                <div
                                  style={{
                                    fontSize: "12px",
                                    marginBottom: "0px",
                                  }}
                                >
                                  {documentConfig?.toData?.shippingAddress
                                    ?.name || ""}
                                </div>
                                <div>
                                  {documentConfig?.toData?.shippingAddress
                                    ?.addressline1 || ""}{" "}
                                  {documentConfig?.toData?.shippingAddress
                                    ?.addressline2 || ""}{" "}
                                </div>
                                <div>
                                  {documentConfig?.toData?.shippingAddress
                                    ?.state || ""}{" "}
                                  {documentConfig?.toData?.shippingAddress
                                    ?.district || ""}{" "}
                                  {
                                    documentConfig?.toData?.shippingAddress
                                      ?.city
                                  }{" "}
                                  {documentConfig?.toData?.shippingAddress
                                    ?.country || ""}{" "}
                                  -{" "}
                                  {documentConfig?.toData?.shippingAddress
                                    ?.pincode || ""}
                                </div>
                                {/* <div>
                                    {" "}
                                    Phone: +91 {data.saleto?.phone || ""}
                                  </div>
                                  <div>
                                    {" "}
                                    Email {data.saleto?.email || ""}
                                  </div>
                                  {data.saleto?.gstnumber && (
                                    <div>
                                      <span>GSTIN:</span>{" "}
                                      {data.saleto?.gstnumber || ""}
                                    </div>
                                  )}
                                   {data.saleto?.pannumber && (
                                    <div>
                                      <span>PAN:</span>{" "}
                                      {data.saleto?.pannumber || ""}
                                    </div>
                                  )}
                                  <div>CIN :</div> */}
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  )}

                  <table
                    // className="items"
                    width="100%"
                    style={{
                      marginTop: "6px",
                      borderCollapse: "collapse",
                    }}
                    cellPadding="4"
                  >
                    <thead>
                      <tr>
                        <th
                          className="force-style"
                          style={{
                            width: "20px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          S.No
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "240px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Description
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "120px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          HSN/SAC
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "50px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          MRP
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "100px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Base Price
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "100px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Qty
                        </th>

                        <th
                          className="force-style"
                          style={{
                            width: "30px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Tax Val
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "100px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Tax
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "100px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Tax Amt
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "100px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {documentConfig?.items?.map((product, index) => (
                        <tr key={index}>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {index + 1}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "left",
                              fontSize: "12px",
                              width: "30%",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            <div>{product?.title}</div>
                            {/* {product?.imode === "SERVICE" && (
                                      <div className="ms-0 fsz-10 lh-13 mb-1">
                                        {product?.description}
                                      </div>
                                    )} */}
                            {product.imode === "PRODUCT" && product.note && (
                              <div className="ms-0 fsz-10 mb-1">
                                {product?.note}
                              </div>
                            )}
                            {/* {product.hsnsac && (
                                      <div>
                                        <span className="fw-medium">
                                          {" "}
                                          {product.imode === "SERVICE" ? "SAC:" : "HSN:"}
                                        </span>{" "}
                                        {product.hsnsac}
                                      </div>
                                    )}
                                    {product.discount > 0 && (
                                      <>
                                        <div>
                                          <span className="fw-medium">MRP : </span> 
                                          {CurrencyConverter.toCurrency(
                                            product.mrp || 0,
                                            "INR"
                                          )}
                                        </div>
                                        <div>
                                          <span className="fw-medium">Discount : </span> -
                                          
                                          {CurrencyConverter.toCurrency(
                                            product.discount || 0,
                                            "INR"
                                          )}
                                        </div>
                                      </>
                                    )} */}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product?.hsnsac}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product?.mrp}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product?.baseprice}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product?.quantity}
                            <span className="font-11 ms-1">
                              {(() => {
                                const unitCode = product?.isSecondaryUnit
                                  ? product?.unitConfig?.secondaryUnitCode
                                  : product?.unitConfig?.primaryUnitCode;

                                // return unitCode !== "unit" ? unitCode : "";
                                return unitCode || "";
                              })()}
                            </span>
                          </td>

                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product?.lineTotalBasePrice}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {`${product?.taxpercentage}%`}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product?.lineTotalTaxAmount}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product?.lineGrandTotal}
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan="6">
                          Total Items : {documentConfig?.items?.length}
                        </td>
                        <td colSpan="3">Total Amount Before Tax :</td>
                        <td
                          style={{
                            textAlign: "end",
                            fontSize: "12px",
                            fontWeight: 500,
                          }}
                        >
                          {documentConfig?.totalBasePrice}{" "}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="6" style={{ verticalAlign: "top" }}>
                          {hasPayment && (
                              <table
                                className="items"
                                width="50%"
                                cellPadding="4"
                              >
                                <thead>
                                  <tr>
                                    <th
                                      colSpan={2}
                                      className="force-style"
                                      style={{
                                        width: "100%",
                                        textAlign: "center",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        "--th-bg":
                                          selectedTheme?.templateTheme
                                            ?.primaryColor,
                                        "--th-color":
                                          selectedTheme?.templateTheme
                                            ?.foregroundColor,
                                      }}
                                    >
                                      Payment Mode
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {parseAmount(documentConfig?.cashPaid) > 0 && (
                                    <tr>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          width: "10%",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        Cash
                                      </td>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          fontWeight: "600",
                                          width: "10%",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        {documentConfig?.cashPaid}
                                      </td>
                                    </tr>
                                  )}
                                  {parseAmount(documentConfig?.cardPaid) > 0 && (
                                    <tr>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          width: "10%",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        Card
                                      </td>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          fontWeight: "600",
                                          width: "10%",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        {documentConfig?.cardPaid}
                                      </td>
                                    </tr>
                                  )}
                                  {parseAmount(documentConfig?.onlinePaid) > 0 && (
                                    <tr>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          width: "10%",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        Online Amount
                                      </td>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          width: "10%",
                                          fontWeight: "600",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        {documentConfig?.onlinePaid}
                                      </td>
                                    </tr>
                                  )}
                                  {parseAmount(documentConfig?.upiPaid) > 0 && (
                                    <tr>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          width: "10%",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        UPI
                                      </td>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          width: "10%",
                                          fontWeight: "600",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        {documentConfig?.upiPaid}
                                      </td>
                                    </tr>
                                  )}
                                  {parseAmount(documentConfig?.balanceDue) > 0 && (
                                    <tr>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          width: "10%",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        BalanceDue
                                      </td>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          width: "10%",
                                          fontWeight: "600",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        {documentConfig?.balanceDue}
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            )}
                        </td>
                        <td colSpan="3" style={{ verticalAlign: "top" }}>
                          {isInterState ? (
                            <div>IGST</div>
                          ) : (
                            <>
                              <div>SGST :</div>
                              <div>CGST :</div>
                            </>
                          )}
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: 600,
                            }}
                          >
                            Total Amount :
                          </div>
                        </td>
                        <td
                          style={{
                            textAlign: "end",
                            fontSize: "12px",
                            verticalAlign: "top",
                          }}
                        >
                          {isInterState ? (
                            <div>{documentConfig?.igstAmount}</div>
                          ) : (
                            <>
                              <div> {documentConfig?.sgstAmount}</div>
                              <div>{documentConfig?.cgstAmount}</div>
                            </>
                          )}
                          <div
                            style={{
                              textAlign: "end",
                              fontSize: "14px",
                              fontWeight: 600,
                            }}
                          >
                            {documentConfig?.grandTotal}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="row mt-2">
                    <div className="col-4 p-2 pt-0  border-dark font-11">
                      {documentConfig?.bankDetails && (
                          <>
                            <strong>Bank Details:</strong>
                            {documentConfig?.bankDetails?.accountNo && (
                              <div className="d-flex font-11">
                                <span style={{ width: "80px" }}>
                                  Account #:
                                </span>
                                {documentConfig.bankDetails.accountNo}
                              </div>
                            )}

                            {documentConfig?.bankDetails?.ifsc && (
                              <div className="d-flex font-11">
                                <span style={{ width: "80px" }}>IFSC:</span>
                                {documentConfig.bankDetails.ifsc}
                              </div>
                            )}

                            {documentConfig?.bankDetails?.beneficiaryName && (
                              <div className="d-flex font-11">
                                <span style={{ width: "80px" }}>Name:</span>
                                {documentConfig.bankDetails.beneficiaryName}
                              </div>
                            )}

                            {documentConfig?.bankDetails?.bankName && (
                              <div className="d-flex font-11">
                                <span style={{ width: "80px" }}>
                                  Bank Name:
                                </span>
                                {documentConfig.bankDetails.bankName}
                              </div>
                            )}

                            {documentConfig?.bankDetails?.branch && (
                              <div className="d-flex font-11">
                                <span style={{ width: "80px" }}>
                                  Branch Name:
                                </span>
                                {documentConfig.bankDetails.branch}
                              </div>
                            )}

                            {documentConfig?.upiId && (
                              <div className="d-flex font-11">
                                <span style={{ width: "80px" }}>UPI ID:</span>
                                {documentConfig?.upiId}
                              </div>
                            )}
                          </>
                        )}
                    </div>
                    {qrCodeDataUrl && (
                      <div className="col-3 d-flex align-items-center justify-content-end">
                        <img
                          src={qrCodeDataUrl}
                          alt="QR"
                          style={{
                            width: "100px",
                            height: "100px",
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <table width="100%">
                    <tbody>
                      <tr>
                        {/* Left */}
                        <td
                          width="70%"
                          style={{
                            verticalAlign: "top",
                            paddingRight: "10px",
                          }}
                        >
                          {documentConfig?.remarks ? (
                            <>
                              <div className="font-12 fw-600 text-black mb-1 mt-2">
                                Remark
                              </div>

                              <div
                                className="font-12 mb-2 p-0"
                                style={{
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                }}
                              >
                                {capitalizeFirstLetter(documentConfig?.remarks)}
                              </div>
                            </>
                          ) : null}

                          {terms?.length > 0 && !isPreSale && (
                            <>
                              <div
                                className="fs-13 fw-600 mt-2"
                                style={{
                                  textAlign: "left",
                                  color: "black",
                                }}
                              >
                                Terms & Conditions
                              </div>

                              <div className="terms-box">{terms}</div>
                            </>
                          )}
                        </td>

                        {/* Right */}
                        <td
                          width="30%"
                          style={{
                            verticalAlign: "top",
                            textAlign: "right",
                          }}
                        >
                          {storeSettings?.invoiceSettings?.signature && (
                            <img
                              src={
                                uRLs.IMAGE_URL +
                                storeSettings.invoiceSettings.signature
                              }
                              width={150}
                              alt="Signature"
                            />
                          )}

                          <div>Issued By, Signature</div>

                          <div className="fw-bold">
                            {documentConfig?.fromData?.title}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : showBillDetails ? (
        <>
          <div className="tm_container">
            <div className="tm_invoice_wrap">
              <div className="tm_invoice tm_style1 tm_type1">
                <div className="tm_invoice_in p-2">
                  <div className="row">
                    <div className="col-7 pe-1 ">
                      <div className="border p-2 h-100 d-flex ">
                        <div className="d-flex gap-2 align-items-start">
                          <div className="tm_logo">
                            {documentConfig?.image && (
                              <img
                                src={
                                  documentConfig?.image
                                }
                                className="img-rounded logo mb-0"
                                height="45"
                                width="150"
                                style={{
                                  objectFit: "contain",
                                }}
                              />
                            )}
                          </div>
                          <div>
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                marginBottom: "0px",
                              }}
                            >
                              {documentConfig.fromData?.title || ""}
                            </div>
                            <div>
                              {documentConfig?.fromData?.addressline1 || ""}{" "}
                              {documentConfig?.fromData?.addressline2 ||
                                ""}{" "}
                            </div>
                            <div>
                              {documentConfig?.fromData?.state || ""}{" "}
                              {documentConfig?.fromData?.district || ""}{" "}
                              {documentConfig?.fromData?.country || ""} -{" "}
                              {documentConfig?.fromData?.pincode || ""}
                            </div>                          
                            <div>
                              Phone: +91 {documentConfig?.fromData?.phone || ""}
                              {documentConfig?.fromData?.additionalPhone && (
                                <>
                                  , +91{" "}
                                  {documentConfig.fromData.additionalPhone}
                                </>
                              )}
                            </div>
                            {documentConfig?.fromData?.email && (
                              <div>
                                {" "}
                                Email: {documentConfig?.fromData?.email || ""}
                              </div>
                            )}
                            {documentConfig?.fromData?.gstnumber && (
                              <div>
                                <strong>GSTIN:</strong>{" "}
                                {documentConfig?.fromData?.gstnumber || ""}
                              </div>
                            )}
                            {documentConfig?.fromData?.fssai && (
                              <div>
                                FSSAI:{" "}
                                {documentConfig?.fromData?.fssai || ""}
                              </div>
                            )}
                            {documentConfig?.fromData?.cinnumber && (
                              <div>
                                CIN:{" "}
                                {documentConfig?.fromData?.cinnumber || ""}
                              </div>
                            )}
                            {documentConfig?.fromData?.pannumber && (
                              <div>
                                PAN:{" "}
                                {documentConfig?.fromData?.pannumber || ""}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-5 ps-1 ">
                      <div className="border h-100 p-2">
                        <div className="tm_text_uppercase text-end">
                          {documentConfig.title && (
                            <strong>{documentConfig.title}</strong>
                          )}
                        </div>
                        <div className="pb-1 d-flex">
                          <b style={{ width: "100px" }}>
                            {documentConfig?.numberLabel}
                          </b>
                          <span>: {documentConfig?.number}</span>
                        </div>

                        <div className="pb-1 d-flex">
                          <b style={{ width: "100px" }}>Date</b>
                          <span>: {documentConfig?.Date?.date}</span>
                        </div>

                        <div className="pb-1 d-flex">
                          <b style={{ width: "100px" }}>Place Of Supply</b>
                          <span>: {documentConfig?.fromData?.state || ""}</span>
                        </div>

                        <div className="pb-1 d-flex">
                          <b style={{ width: "100px" }}>Due Date</b>
                          <span>: {documentConfig?.Date?.date}</span>
                        </div>
                        <div className="pb-1 d-flex">
                          <b style={{ width: "100px" }}>Sales Person</b>
                          <span>:{globalData?.salesperson || "-"}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {documentConfig?.toData &&  (
                    <div className="row mt-2">
                      <div className="col-6 pe-1">
                        <div className="border p-2 h-100 ">
                          <div>
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                marginBottom: "0px",
                              }}
                            >
                              {documentConfig?.toLabel}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                marginBottom: "0px",
                              }}
                            >
                              {documentConfig?.toData?.name || ""}
                            </div>
                            <div>
                              {documentConfig?.toData?.addressline1 || ""}{" "}
                              {documentConfig?.toData?.addressline2 || ""}{" "}
                            </div>
                            <div>
                              {documentConfig?.toData?.state || ""}{" "}
                              {documentConfig?.toData?.district || ""}{" "}
                              {/* {documentConfig?.toData?.country || ""} -{" "} */}
                              {documentConfig?.toData?.pincode || ""}
                            </div>
                            <div>
                              {" "}
                              Phone: +91 {documentConfig?.toData?.phone || ""}
                            </div>
                            {documentConfig?.toData?.email && (
                              <div>
                                {" "}
                                Email {documentConfig?.toData?.email || ""}
                              </div>
                            )}
                            {documentConfig?.toData?.referenceid && (
                              <div>
                                <span>Reference No:</span>{" "}
                                {documentConfig?.toData?.referenceid || ""}
                              </div>
                            )}
                            {documentConfig?.toData?.gstnumber && (
                              <div>
                                <strong>GSTIN:</strong>{" "}
                                {documentConfig?.toData?.gstnumber || ""}
                              </div>
                            )}
                            {documentConfig?.toData?.fssai && (
                              <div>
                                FASSI:{" "}
                                {documentConfig?.toData?.fssai || ""}
                              </div>
                            )}
                            {documentConfig?.toData?.pannumber && (
                              <div>
                                PAN:{" "}
                                {documentConfig?.toData?.pannumber || ""}
                              </div>
                            )}
                            {documentConfig?.toData?.cinnumber && (
                              <div>
                                CIN:{" "}
                                {documentConfig?.toData?.cinnumber || ""}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {(documentConfig?.toData?.shippingAddress?.addressline1 ||
                        documentConfig?.toData?.shippingAddress
                          ?.addressline2) && (
                        <div className="col-6 ps-1">
                          <div className="border p-2 h-100 ">
                            <div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  marginBottom: "0px",
                                }}
                              >
                                Shipping Address
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  marginBottom: "0px",
                                }}
                              >
                                {documentConfig?.toData?.shippingAddress?.name}
                              </div>
                              <div>
                                {documentConfig?.toData?.shippingAddress
                                  ?.addressline1 || ""}{" "}
                                {documentConfig?.toData?.shippingAddress
                                  ?.addressline2 || ""}{" "}
                              </div>
                              <div>
                                {documentConfig?.toData?.shippingAddress
                                  ?.state || ""}{" "}
                                {documentConfig?.toData?.shippingAddress
                                  ?.district || ""}{" "}
                                {documentConfig?.toData?.shippingAddress?.city}{" "}
                                {documentConfig?.toData?.shippingAddress
                                  ?.country || ""}{" "}
                                -{" "}
                                {documentConfig?.toData?.shippingAddress
                                  ?.pincode || ""}
                              </div>
                              {/* <div>
                                    {" "}
                                    Phone: {data.saleto?.phone || ""}
                                  </div>
                                  {data.saleto?.gstnumber && (
                                    <div>
                                      <span>GSTIN:</span>{" "}
                                      {data.saleto?.gstnumber || ""}
                                    </div>
                                  )} */}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <table
                    // className="items"
                    width="100%"
                    style={{
                      marginTop: "6px",
                      borderCollapse: "collapse",
                    }}
                    cellPadding="4"
                  >
                    <thead>
                      <tr>
                        <th
                          className="force-style"
                          style={{
                            // width: "10px",
                            textAlign: "center",
                            //   background: "#f3f3f3",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                          }}
                        >
                          S.No
                        </th>
                        <th
                          className="force-style"
                          style={{
                            // width: "10px",
                            textAlign: "center",
                            //   background: "#f3f3f3",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                          }}
                        >
                          Description
                        </th>
                        <th
                          className="force-style"
                          style={{
                            // width: "10px",
                            textAlign: "center",
                            //   background: "#f3f3f3",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                          }}
                        >
                          HSN/SAC
                        </th>
                        <th
                          className="force-style"
                          style={{
                            // width: "10px",
                            textAlign: "center",
                            //   background: "#f3f3f3",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                          }}
                        >
                          MRP
                        </th>
                        <th
                          className="force-style"
                          style={{
                            // width: "10px",
                            textAlign: "center",
                            //   background: "#f3f3f3",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                          }}
                        >
                          Discount
                        </th>
                        <th
                          className="force-style"
                          style={{
                            // width: "10px",
                            textAlign: "center",
                            //   background: "#f3f3f3",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                          }}
                        >
                          Rate
                        </th>
                        <th
                          className="force-style"
                          style={{
                            // width: "10px",
                            textAlign: "center",
                            //   background: "#f3f3f3",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                          }}
                        >
                          Qty
                        </th>

                        <th
                          className="force-style"
                          style={{
                            // width: "10px",
                            textAlign: "center",
                            //   background: "#f3f3f3",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                          }}
                        >
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {documentConfig?.items?.map((product, index) => (
                        <tr key={index}>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              width: "5%",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {index + 1}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "left",
                              fontSize: "12px",
                              width: "65%",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            <div>{product.title}</div>
                            {/* {product.imode === "SERVICE" && (
                                      <div className="ms-0 fsz-10 lh-13 mb-1">
                                        {product.description}
                                      </div>
                                    )} */}
                            {product.imode === "PRODUCT" && product.note && (
                              <div className="ms-0 fs-12 text-truncate-description text-muted">
                                {product?.note}
                              </div>
                            )}

                            {/* <div>
                                      {product.hsnsac && (
                                        <span>
                                          <span className="fw-medium">
                                            HSN/SAC :{" "}
                                          </span>{" "}
                                          {product.hsnsac}
                                        </span>
                                      )}
                                      {product.discount > 0 && (
                                        <>
                                          <>
                                            {" "}
                                            <span className="fw-medium">
                                              MRP :{" "}
                                            </span>{" "}
                                            
                                            {CurrencyConverter.toCurrency(
                                              product.mrp || 0,
                                              "INR"
                                            )}
                                          </>{" "}
                                          <>
                                            <span className="fw-medium">
                                              Discount :{" "}
                                            </span>{" "}
                                             
                                            {CurrencyConverter.toCurrency(
                                              product.discount || 0,
                                              "INR"
                                            )}
                                          </>
                                        </>
                                      )}{" "}
                                      {product.taxpercentage > 0 && (
                                        <>
                                          <span className="fw-medium">
                                            Tax :{" "}
                                          </span>{" "}
                                          
                                          {CurrencyConverter.toCurrency(
                                            product.baseprice || 0,
                                            "INR"
                                          )}
                                          @{product.taxpercentage}% = 
                                          {CurrencyConverter.toCurrency(
                                            product.taxamount || 0,
                                            "INR"
                                          )}
                                        </>
                                      )}
                                    </div> */}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product.hsnsac}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product.mrp}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product.discount}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              width: "10%",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product.soldprice}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              width: "10%",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product.quantity}
                            <span className="font-11 ms-1">
                              {(() => {
                                const unitCode = product?.isSecondaryUnit
                                  ? product?.unitConfig?.secondaryUnitCode
                                  : product?.unitConfig?.primaryUnitCode;

                                // return unitCode !== "unit" ? unitCode : "";
                                return unitCode || "";
                              })()}
                            </span>
                          </td>

                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              width: "10%",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product.lineGrandTotal}
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan="4">
                          Total Items : {documentConfig?.items?.length}
                        </td>
                        <td
                          colSpan="3"
                          style={{
                            textAlign: "end",
                            fontSize: "14px",
                            fontWeight: 600,
                          }}
                        >
                          Total Amount :
                        </td>
                        <td
                          style={{
                            textAlign: "end",
                            fontSize: "14px",
                            fontWeight: 600,
                          }}
                        >
                          {documentConfig?.grandTotal}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="7" style={{ verticalAlign: "top" }}>
                          {hasPayment && (
                              <table
                                className="items"
                                width="50%"
                                cellPadding="4"
                              >
                                <thead>
                                  <tr>
                                    <th
                                      colSpan={2}
                                      className="force-style"
                                      style={{
                                        width: "100%",
                                        textAlign: "center",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        "--th-bg":
                                          selectedTheme?.templateTheme
                                            ?.primaryColor,
                                        "--th-color":
                                          selectedTheme?.templateTheme
                                            ?.foregroundColor,
                                      }}
                                    >
                                      Payment Mode
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {parseAmount(documentConfig?.cashPaid) > 0 && (
                                    <tr>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          width: "10%",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        Cash
                                      </td>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          fontWeight: "600",
                                          width: "10%",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        {documentConfig?.cashPaid}
                                      </td>
                                    </tr>
                                  )}
                                  {parseAmount(documentConfig?.cardPaid) > 0 && (
                                    <tr>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          width: "10%",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        Card
                                      </td>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          fontWeight: "600",
                                          width: "10%",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        {documentConfig?.cardPaid}
                                      </td>
                                    </tr>
                                  )}
                                  {parseAmount(documentConfig?.onlinePaid) > 0 && (
                                    <tr>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          width: "10%",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        Online Amount
                                      </td>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          width: "10%",
                                          fontWeight: "600",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        {documentConfig?.onlinePaid}
                                      </td>
                                    </tr>
                                  )}
                                  {parseAmount(documentConfig?.upiPaid) > 0 && (
                                    <tr>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          width: "10%",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        UPI
                                      </td>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          width: "10%",
                                          fontWeight: "600",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        {documentConfig?.upiPaid}
                                      </td>
                                    </tr>
                                  )}
                                  {parseAmount(documentConfig?.balanceDue) > 0 && (
                                    <tr>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          width: "10%",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        BalanceDue
                                      </td>
                                      <td
                                        className="force-style"
                                        style={{
                                          textAlign: "start",
                                          fontSize: "12px",
                                          width: "10%",
                                          fontWeight: "600",
                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                        }}
                                      >
                                        {documentConfig?.balanceDue}
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="row mt-2">
                    <div className="col-4 p-2 pt-0  border-dark font-11">
                      {documentConfig?.bankDetails && (
                          <>
                            <strong>Bank Details:</strong>
                            {documentConfig?.bankDetails?.accountNo && (
                              <div className="d-flex font-11">
                                <span style={{ width: "80px" }}>
                                  Account #:
                                </span>
                                {documentConfig.bankDetails.accountNo}
                              </div>
                            )}

                            {documentConfig?.bankDetails?.ifsc && (
                              <div className="d-flex font-11">
                                <span style={{ width: "80px" }}>IFSC:</span>
                                {documentConfig.bankDetails.ifsc}
                              </div>
                            )}

                            {documentConfig?.bankDetails?.beneficiaryName && (
                              <div className="d-flex font-11">
                                <span style={{ width: "80px" }}>Name:</span>
                                {documentConfig.bankDetails.beneficiaryName}
                              </div>
                            )}

                            {documentConfig?.bankDetails?.bankName && (
                              <div className="d-flex font-11">
                                <span style={{ width: "80px" }}>
                                  Bank Name:
                                </span>
                                {documentConfig.bankDetails.bankName}
                              </div>
                            )}

                            {documentConfig?.bankDetails?.branch && (
                              <div className="d-flex font-11">
                                <span style={{ width: "80px" }}>
                                  Branch Name:
                                </span>
                                {documentConfig.bankDetails.branch}
                              </div>
                            )}

                            {documentConfig?.upiId && (
                              <div className="d-flex font-11">
                                <span style={{ width: "80px" }}>UPI ID:</span>
                                {documentConfig?.upiId}
                              </div>
                            )}
                          </>
                        )}
                    </div>
                    {qrCodeDataUrl &&  (
                      <div className="col-3 d-flex align-items-center justify-content-end">
                        <img
                          src={qrCodeDataUrl}
                          alt="QR"
                          style={{
                            width: "100px",
                            height: "100px",
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <table width="100%">
                    <tbody>
                      <tr>
                        {/* Left */}
                        <td
                          width="70%"
                          style={{
                            verticalAlign: "top",
                            paddingRight: "10px",
                          }}
                        >
                          {documentConfig?.remarks ? (
                            <>
                              <div className="font-12 fw-600 text-black mb-1 mt-2">
                                Remark
                              </div>

                              <div
                                className="font-12 mb-2 p-0"
                                style={{
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                }}
                              >
                                {capitalizeFirstLetter(documentConfig?.remarks)}
                              </div>
                            </>
                          ) : null}

                          {terms?.length > 0 && !isPreSale && (
                            <>
                              <div
                                className="fs-13 fw-600 mt-2"
                                style={{
                                  textAlign: "left",
                                  color: "black",
                                }}
                              >
                                Terms & Conditions
                              </div>

                              <div className="terms-box">{terms}</div>
                            </>
                          )}
                        </td>

                        {/* Right */}
                        <td
                          width="30%"
                          style={{
                            verticalAlign: "top",
                            textAlign: "right",
                          }}
                        >
                          {storeSettings?.invoiceSettings?.signature && (
                            <img
                              src={
                                uRLs.IMAGE_URL +
                                storeSettings.invoiceSettings.signature
                              }
                              width={150}
                              alt="Signature"
                            />
                          )}

                          <div>Issued By, Signature</div>

                          <div className="fw-bold">
                            {documentConfig?.fromData?.title}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="default-content">
          <p>No content available for this sale mode.</p>
        </div>
      )}
    </>
  );
};
export default StructuredInvoice;
